/**
 * useExpiryColumns.tsx
 *
 * Returns the standard column definitions for the expiry-items DataTable.
 * Both Dashboard and ExpiryItems share this hook so the columns stay in sync
 * and handlers are wired in one place.
 *
 * The hook is in .tsx (not .ts) because column `render` functions return JSX.
 */

import React, { useMemo } from "react";
import type { ExpiryItem } from "./useExpiryItems";
import type { TableColumn } from "../types";
import { getItemStatus } from "../utils/expiryUtils";
import { useReminderSettings } from "./useSettings";
import StatusBadge from "../components/StatusBadge";
import DocumentNameCell from "../components/DocumentNameCell";
import ExpiryDateCell from "../components/ExpiryDateCell";
import NextReminderCell from "../components/NextReminderCell";
import OwnerCell from "../components/OwnerCell";
import ActionCell from "../components/ActionCell";

// ─── Props ────────────────────────────────────────────────────────────────────

interface ColumnHandlers {
  onAddFields?: (item: ExpiryItem) => void;
  onRenew?: (item: ExpiryItem) => void;
  onSendReminder: (item: ExpiryItem) => void;
  /** Called when the user clicks the document name cell — opens HistoryDrawer */
  onRowClick: (item: ExpiryItem) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a memoised array of `TableColumn<ExpiryItem>` used by both the
 * Dashboard and ExpiryItems pages.
 *
 * Pass the three contextual action handlers so `ActionCell` can call back into
 * the page's state without re-creating columns on every render.
 */
export function useExpiryColumns(
  handlers: ColumnHandlers
): TableColumn<ExpiryItem>[] {
  const { onAddFields, onRenew, onSendReminder, onRowClick } = handlers;

  // Pull the user's configured reminder schedule so the "Next Reminder" column
  // reflects their actual settings rather than the built-in defaults.
  const { data: reminderSettings } = useReminderSettings();
  const reminderDays = reminderSettings?.reminder_days;

  return useMemo(
    () => [
      {
        key: "name",
        label: "Document",
        sortable: true,
        render: (_value, row) => (
          <DocumentNameCell
            name={row.name}
            onClick={(e?: React.MouseEvent) => {
              e?.stopPropagation();
              onRowClick(row);
            }}
          />
        ),
      },
      {
        key: "category",
        label: "Category",
        sortable: true,
        render: (value) => (
          <span className="text-[#6B7280]">{(value as string) || "—"}</span>
        ),
      },
      {
        key: "expiry_date",
        label: "Expiry Date",
        sortable: true,
        render: (value, row) => (
          <ExpiryDateCell value={value as string | null} item={row} />
        ),
      },
      {
        key: "next_reminder",
        label: "Next Reminder",
        sortable: false,
        render: (_value, row) => (
          <NextReminderCell item={row} reminderDays={reminderDays} />
        ),
      },
      {
        key: "owner",
        label: "Owner",
        sortable: false,
        render: (_value, row) => (
          <OwnerCell
            owners={(row as ExpiryItem & { owners?: unknown[] }).owners ?? []}
          />
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: false,
        render: (_value, row) => {
          const s = getItemStatus(row);
          return <StatusBadge status={s.status} label={s.label} />;
        },
      },
      {
        key: "action",
        label: "Action",
        sortable: false,
        render: (_value, row) => (
          <ActionCell
            item={row}
            onAddFields={onAddFields}
            onRenew={onRenew}
            onSendReminder={onSendReminder}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAddFields, onRenew, onSendReminder, onRowClick, reminderDays]
  );
}
